import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from 'react'
import { usePlayerContext } from 'context/player'
import {
  play,
  pause,
  forwardTrack,
  backTrack,
  selectChapter,
  updateRate
} from 'context/player/reducer/player.actions'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import { useTranslation } from 'i18n'
import { useSpeechRecognition } from './useSpeechRecognition'
import {
  matchCommand,
  recognitionLang,
  resolveLocale,
  type SupportedLocale
} from './commandRegistry'
import { isVoiceControlBrowserSupported } from './browserSupport'
import {
  getStoredVoiceEnabled,
  setStoredVoiceEnabled,
  hasSeenVoiceHelp,
  markVoiceHelpSeen
} from './voicePreferences'
import {
  type RecognitionErrorKind,
  type RecognitionStatus,
  type VoiceIntent
} from './voiceControl.types'

const REPEAT_SPACING_MS = 150
const PLAYBACK_RATES = [1, 1.25, 1.5, 1.75, 2] as const

interface VoiceControlContextValue {
  isSupported: boolean
  isFlagEnabled: boolean
  isEnabled: boolean
  status: RecognitionStatus
  lastIntent: VoiceIntent | null
  error: RecognitionErrorKind | null
  locale: SupportedLocale
  isHelpOpen: boolean
  isConfirmOpen: boolean
  enable: () => void
  disable: () => void
  openHelp: () => void
  closeHelp: () => void
  openConfirm: () => void
  closeConfirm: () => void
}

const INERT_VALUE: VoiceControlContextValue = {
  isSupported: false,
  isFlagEnabled: false,
  isEnabled: false,
  status: 'idle',
  lastIntent: null,
  error: null,
  locale: 'pt-BR',
  isHelpOpen: false,
  isConfirmOpen: false,
  enable: () => undefined,
  disable: () => undefined,
  openHelp: () => undefined,
  closeHelp: () => undefined,
  openConfirm: () => undefined,
  closeConfirm: () => undefined
}

const VoiceControlContext = createContext<VoiceControlContextValue | null>(null)

const VoiceControlProvider = ({ children }: PropsWithChildren) => {
  const { featureToggles } = useFeatureToggles()
  const flags = (featureToggles ?? {}) as Record<string, boolean | undefined>
  const devForce =
    process.env.NODE_ENV !== 'production' &&
    typeof localStorage !== 'undefined' &&
    localStorage.getItem('voiceControlForce') === '1'
  const isFlagEnabled =
    devForce || Boolean(flags.readerVoiceControl || flags.reader_voice_control)

  const { state, dispatch } = usePlayerContext()
  const { i18n } = useTranslation()
  const locale = resolveLocale(i18n.language)

  const [isEnabled, setIsEnabled] = useState(getStoredVoiceEnabled)
  const [status, setStatus] = useState<RecognitionStatus>('idle')
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [lastIntent, setLastIntent] = useState<VoiceIntent | null>(null)
  const [error, setError] = useState<RecognitionErrorKind | null>(null)
  const recognizedTimer = useRef<ReturnType<typeof setTimeout>>()
  const repeatTimers = useRef<ReturnType<typeof setTimeout>[]>([])

  const stepRate = useCallback(
    (direction: 1 | -1) => {
      const rates = PLAYBACK_RATES as readonly number[]
      const current = state?.rate ?? 1
      const idx = rates.indexOf(current)
      const baseIdx = idx === -1 ? rates.indexOf(1) : idx
      const nextIdx = Math.min(
        Math.max(baseIdx + direction, 0),
        rates.length - 1
      )
      dispatch?.(updateRate(rates[nextIdx]))
    },
    [dispatch, state?.rate]
  )

  const goChapter = useCallback(
    (direction: 1 | -1) => {
      const chapters = state?.audioBooks ?? []
      if (!chapters.length) return
      const currentIdx = chapters.findIndex(
        chapter => chapter.id === state?.currentChapter?.id
      )
      const targetIdx = currentIdx + direction
      if (targetIdx < 0 || targetIdx >= chapters.length) return
      dispatch?.(selectChapter(chapters[targetIdx].id))
    },
    [dispatch, state?.audioBooks, state?.currentChapter?.id]
  )

  const runIntent = useCallback(
    (intent: VoiceIntent) => {
      switch (intent) {
        case 'play':
          dispatch?.(play())
          break
        case 'pause':
          dispatch?.(pause())
          break
        case 'stop':
          dispatch?.(pause())
          break
        case 'forward':
          dispatch?.(forwardTrack(true))
          break
        case 'backward':
          dispatch?.(backTrack(true))
          break
        case 'nextChapter':
          goChapter(1)
          break
        case 'prevChapter':
          goChapter(-1)
          break
        case 'faster':
          stepRate(1)
          break
        case 'slower':
          stepRate(-1)
          break
      }
    },
    [dispatch, goChapter, stepRate]
  )

  const onTranscript = useCallback(
    (transcript: string) => {
      const command = matchCommand(transcript, locale)
      if (!command) return
      const { intent, repeat } = command
      runIntent(intent)
      for (let run = 1; run < repeat; run++) {
        const timer = setTimeout(() => {
          repeatTimers.current = repeatTimers.current.filter(
            pending => pending !== timer
          )
          runIntent(intent)
        }, run * REPEAT_SPACING_MS)
        repeatTimers.current.push(timer)
      }
      setLastIntent(intent)
      setStatus('recognized')
      clearTimeout(recognizedTimer.current)
      recognizedTimer.current = setTimeout(() => setStatus('listening'), 1200)
    },
    [locale, runIntent]
  )

  const onError = useCallback((kind: RecognitionErrorKind) => {
    setError(kind)
    if (kind === 'not-allowed' || kind === 'audio-capture') {
      setIsEnabled(false)
      setStoredVoiceEnabled(false)
      setStatus('error')
    }
  }, [])

  const {
    isSupported: apiSupported,
    start,
    stop
  } = useSpeechRecognition({
    lang: recognitionLang(locale),
    onTranscript,
    onError
  })
  const browserSupported = useMemo(() => isVoiceControlBrowserSupported(), [])
  const isSupported = devForce || (apiSupported && browserSupported)

  const enable = useCallback(() => {
    setError(null)
    setIsEnabled(true)
    setStoredVoiceEnabled(true)
    if (!hasSeenVoiceHelp()) {
      markVoiceHelpSeen()
      setIsHelpOpen(true)
    }
  }, [])
  const disable = useCallback(() => {
    setIsEnabled(false)
    setStoredVoiceEnabled(false)
    setIsHelpOpen(false)
  }, [])
  const openHelp = useCallback(() => setIsHelpOpen(true), [])
  const closeHelp = useCallback(() => setIsHelpOpen(false), [])
  const openConfirm = useCallback(() => setIsConfirmOpen(true), [])
  const closeConfirm = useCallback(() => setIsConfirmOpen(false), [])

  const isPlayerOpen = Boolean(state?.isOpen)
  useEffect(() => {
    if (isEnabled && isFlagEnabled && isSupported && isPlayerOpen) {
      start()
      setStatus('listening')
    } else {
      repeatTimers.current.forEach(clearTimeout)
      repeatTimers.current = []
      stop()
      setStatus('idle')
    }
  }, [isEnabled, isFlagEnabled, isSupported, isPlayerOpen, start, stop])

  useEffect(
    () => () => {
      clearTimeout(recognizedTimer.current)
      repeatTimers.current.forEach(clearTimeout)
    },
    []
  )

  const value = useMemo<VoiceControlContextValue>(
    () => ({
      isSupported,
      isFlagEnabled,
      isEnabled,
      status,
      lastIntent,
      error,
      locale,
      isHelpOpen,
      isConfirmOpen,
      enable,
      disable,
      openHelp,
      closeHelp,
      openConfirm,
      closeConfirm
    }),
    [
      isSupported,
      isFlagEnabled,
      isEnabled,
      status,
      lastIntent,
      error,
      locale,
      isHelpOpen,
      isConfirmOpen,
      enable,
      disable,
      openHelp,
      closeHelp,
      openConfirm,
      closeConfirm
    ]
  )

  return (
    <VoiceControlContext.Provider value={value}>
      {children}
    </VoiceControlContext.Provider>
  )
}

export const useVoiceControl = (): VoiceControlContextValue => {
  const ctx = useContext(VoiceControlContext)
  return ctx ?? INERT_VALUE
}

export default VoiceControlProvider
